import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { govPageContainer } from '../ui/PatientUIGovernor';
import { searchCareServices, getSearchSuggestions } from './care-services/careSearch';
import { useStore } from '../state/patientProductionStore';
import { patientApi, type PrescriptionRecord } from '../api';

const CARE_ACTIONS = [
  { label: 'Find Doctor', icon: '🩺', route: '/patient/care-services/doctors', color: T.teal },
  { label: 'Book Lab Test', icon: '🧪', route: '/patient/care-services/labs', color: T.navy },
  { label: 'Order Medicine', icon: '💊', route: '/patient/care-services/pharmacy', color: '#059669' },
  { label: 'Book Imaging', icon: '🩻', route: '/patient/care-services/imaging', color: T.purple },
  { label: 'Ambulance', icon: '🚑', route: '/patient/care-services/ambulance', color: '#DC2626' },
  { label: 'Insurance', icon: '🛡️', route: '/patient/care-services/insurance', color: T.info },
];

const RECORD_KEYWORDS: { keywords: string[]; label: string; route: string }[] = [
  { keywords: ['prescription', 'rx', 'prescribed'], label: 'Prescriptions', route: '/patient/prescriptions' },
  { keywords: ['lab report', 'test result', 'blood report'], label: 'Lab Reports', route: '/patient/lab-reports' },
  { keywords: ['imaging report', 'scan report', 'x-ray report', 'mri report'], label: 'Imaging Reports', route: '/patient/diagnostics' },
  { keywords: ['certificate', 'fitness', 'medical certificate'], label: 'Certificates', route: '/patient/certificates' },
  { keywords: ['vaccine', 'vaccination', 'immunization'], label: 'Vaccinations', route: '/patient/vaccinations' },
  { keywords: ['document', 'record', 'health vault'], label: 'Health Vault', route: '/patient/health-vault' },
  { keywords: ['timeline', 'history', 'activity'], label: 'Timeline', route: '/patient/timeline' },
  { keywords: ['medicine', 'reminder', 'dose'], label: 'Medicine Reminders', route: '/patient/medicines' },
  { keywords: ['appointment', 'follow-up', 'visit'], label: 'Appointments', route: '/patient/appointments' },
  { keywords: ['pharmacy order', 'order status'], label: 'Pharmacy Orders', route: '/patient/pharmacy' },
];

const TYPE_COLORS: Record<string, string> = {
  doctor: T.teal,
  lab: T.navy,
  pharmacy: '#059669',
  imaging: T.purple,
  ambulance: '#DC2626',
  hospital: T.navy,
  insurance: T.info,
  telemedicine: T.teal,
  record: '#6B7280',
};

export default function PatientSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const pharmacyOrders = useStore(s => s.pharmacyOrders);
  const medicineReminders = useStore(s => s.medicineReminders);
  const [prescriptions, setPrescriptions] = useState<PrescriptionRecord[]>([]);
  // REAL provider matches from the backend search index — the search bar is
  // not just a keyword→route mapping anymore.
  const [providerHits, setProviderHits] = useState<{ label: string; route: string; type: string }[]>([]);

  useEffect(() => {
    let alive = true;
    patientApi.getPrescriptions().then((rows) => { if (alive) setPrescriptions(rows); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setProviderHits([]); return; }
    let alive = true;
    const t = setTimeout(() => {
      patientApi.getMarketplaceProviders({ search: q, limit: 5 })
        .then((res) => {
          if (!alive) return;
          setProviderHits((res.providers || []).slice(0, 5).map((p) => ({
            label: `${p.name}${p.specialties?.[0] ? ` · ${p.specialties[0]}` : ''}${p.city ? ` · ${p.city}` : ''}`,
            route: p.type === 'DOCTOR' ? `/patient/care-services/doctors/${p.slug}`
              : p.type === 'HOSPITAL' ? `/patient/care-services/hospitals/${p.slug}`
              : `/patient/providers/${p.slug}`,
            type: p.type.toLowerCase(),
          })));
        })
        .catch(() => { if (alive) setProviderHits([]); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [query]);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];

    const careResults = searchCareServices(query);

    const recordResults = RECORD_KEYWORDS
      .filter(r => r.keywords.some(k => k.includes(q) || q.includes(k)))
      .map(r => ({ type: 'record' as const, label: r.label, route: r.route, urgent: false }));

    return [
      ...providerHits.map(h => ({ type: 'provider' as const, label: h.label, route: h.route, urgent: false })),
      ...careResults.slice(0, 5),
      ...recordResults.slice(0, 3),
    ];
  }, [query, providerHits]);

  const suggestions = useMemo(() => {
    if (query.trim().length < 1) return [];
    return getSearchSuggestions(query.trim()).slice(0, 6);
  }, [query]);

  const recentActivity = useMemo(() => {
    const items: { label: string; route: string; type: string }[] = [];
    if (prescriptions.length > 0) {
      const rx = prescriptions[0];
      items.push({ label: `${rx.doctorName} — Prescription`, route: '/patient/prescriptions', type: 'prescription' });
    }
    if (pharmacyOrders.length > 0) {
      const o = pharmacyOrders[0];
      items.push({ label: `${o.pharmacyName} — ${o.status}`, route: '/patient/pharmacy', type: 'pharmacy' });
    }
    if (medicineReminders.filter(r => r.active).length > 0) {
      const active = medicineReminders.filter(r => r.active);
      items.push({ label: `${active.length} active medicine reminder${active.length > 1 ? 's' : ''}`, route: '/patient/medicines', type: 'medicine' });
    }
    if (items.length === 0) {
      items.push(
        { label: 'View Prescriptions', route: '/patient/prescriptions', type: 'prescription' },
        { label: 'View Lab Reports', route: '/patient/lab-reports', type: 'lab' },
        { label: 'Medicine Reminders', route: '/patient/medicines', type: 'medicine' },
        { label: 'Health Vault', route: '/patient/health-vault', type: 'record' },
      );
    }
    return items;
  }, [prescriptions, pharmacyOrders, medicineReminders]);

  const handleResultClick = (route: string) => {
    window.location.href = route;
  };

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Search input with QR */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: T.ice, borderRadius: 14, border: `1px solid ${T.border}` }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></svg>
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search doctors, tests, medicines, records…"
          style={{
            flex: 1, fontSize: 13, color: T.text, fontFamily: T.font,
            background: 'transparent', border: 'none', outline: 'none',
          }}
        />
        {query && (
          <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18 M6 6l12 12" /></svg>
          </button>
        )}
        <button onClick={() => navigate('/patient/scan')} style={{ width: 34, height: 34, borderRadius: 10, background: T.navy, border: 'none', display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="3" height="3" /><path d="M14 21h3 M21 14v3 M21 21h.01" /></svg>
        </button>
      </div>

      {/* Smart search results */}
      {searchResults.length > 0 && (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: T.shadow }}>
          {searchResults.map((r, i) => (
            <div
              key={i}
              onClick={() => handleResultClick(r.route)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                cursor: 'pointer', borderBottom: i < searchResults.length - 1 ? `1px solid ${T.border}` : 'none',
                background: r.urgent ? '#FEF2F2' : 'transparent',
              }}
            >
              <span style={{
                fontFamily: T.font, fontSize: 10, fontWeight: 600, color: T.white,
                background: TYPE_COLORS[r.type] || T.teal,
                borderRadius: 6, padding: '2px 8px', textTransform: 'capitalize', flexShrink: 0,
              }}>
                {r.type === 'record' ? 'Records' : r.type.charAt(0).toUpperCase() + r.type.slice(1)}
              </span>
              <span style={{ fontFamily: T.font, fontSize: 12, color: T.text, fontWeight: 500, flex: 1 }}>{r.label}</span>
              {r.urgent && (
                <span style={{ fontFamily: T.font, fontSize: 9, color: '#DC2626', fontWeight: 600 }}>⚠️</span>
              )}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round"><path d="m9 18 6-6-6-6" /></svg>
            </div>
          ))}
        </div>
      )}

      {/* Suggestions while typing */}
      {query.trim().length >= 1 && searchResults.length === 0 && suggestions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => setQuery(s)}
              style={{
                fontFamily: T.font, fontSize: 11, color: T.teal, fontWeight: 500,
                background: `${T.teal}10`, border: `1px solid ${T.teal}30`,
                borderRadius: 20, padding: '5px 12px', cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Care Action Cards */}
      {!query.trim() && (
        <>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 10 }}>
              Care Actions
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {CARE_ACTIONS.map(c => (
                <button
                  key={c.label}
                  onClick={() => handleResultClick(c.route)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    padding: '14px 6px', background: T.white, border: `1px solid ${T.border}`,
                    borderRadius: 14, cursor: 'pointer', boxShadow: T.shadow,
                  }}
                >
                  <span style={{ fontSize: 22 }}>{c.icon}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: c.color, fontFamily: T.font, textAlign: 'center', lineHeight: 1.3 }}>{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 10 }}>
              Recent Activity
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {recentActivity.map((r, i) => (
                <button
                  key={i}
                  onClick={() => handleResultClick(r.route)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', background: T.white, border: `1px solid ${T.border}`,
                    borderRadius: 12, cursor: 'pointer', textAlign: 'left', width: '100%',
                  }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: `${TYPE_COLORS[r.type] || T.teal}12`,
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TYPE_COLORS[r.type] || T.teal} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                  </div>
                  <span style={{ fontSize: 11, color: T.text, fontFamily: T.font, fontWeight: 500, flex: 1 }}>{r.label}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2" strokeLinecap="round"><path d="m9 18 6-6-6-6" /></svg>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* No results */}
      {query.trim().length >= 2 && searchResults.length === 0 && suggestions.length === 0 && (
        <div style={{ textAlign: 'center', padding: 24, fontSize: 12, color: T.textTer, fontFamily: T.font }}>
          No results for "{query}"
        </div>
      )}
    </div>
  );
}
